import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, MessageCircle } from "lucide-react";
import { api, whatsappLink } from "@/lib/api";

export default function OrderConfirmation() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center text-[#1B3B36]"><Loader2 className="animate-spin" /></div>;
  }
  if (!order) {
    return (
      <div className="max-w-3xl mx-auto text-center py-32 px-6">
        <h1 className="font-serif text-5xl text-[#1B3B36]">Order not found.</h1>
        <Link to="/" className="mt-8 inline-flex bg-[#1B3B36] text-[#F9F8F6] px-8 py-4 text-[12px] tracking-[0.22em] uppercase">Back home</Link>
      </div>
    );
  }

  const isPaid = order.status === "paid";
  const isTest = order.status === "pending_payment";

  const wa = whatsappLink(
    `Hi Iryaa Homes, I've just placed order #${order.id.slice(0,8)} (₹${order.total.toLocaleString("en-IN")}).`
  );

  return (
    <div data-testid="order-confirmation-page">
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-10 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-[#1B3B36] text-[#F9F8F6] flex items-center justify-center">
          <CheckCircle2 size={26} />
        </div>
        <div className="overline mt-6">{isPaid ? "Payment received" : "Order placed"}</div>
        <h1 className="mt-4 font-serif text-5xl md:text-6xl font-light text-[#1B3B36] leading-[1.02] tracking-tight">
          Thank you, {order.customer.name.split(" ")[0]}.
        </h1>
        <p className="mt-6 text-[15px] text-[#4A4A4A] font-light max-w-xl mx-auto">
          Order <b>#{order.id.slice(0, 8)}</b> has been {isPaid ? "confirmed" : "recorded"}. A copy is on its way to <b>{order.customer.email}</b>. We'll write again when your pieces leave the atelier.
        </p>
        {isTest && (
          <div className="mt-6 inline-block bg-[#EFECE5] border border-[#D8D4CC] px-5 py-3 text-[12px] text-[#4A4A4A] max-w-xl">
            <b>Test mode.</b> Razorpay isn't wired up yet, so no payment was taken. Configure Razorpay keys in the admin panel or `.env` to accept real payments.
          </div>
        )}
      </section>

      <section className="max-w-3xl mx-auto px-6 md:px-12 lg:px-16 pb-16">
        <div className="border border-[#D8D4CC] bg-white p-6 md:p-8">
          <div className="overline">Your pieces</div>
          <div className="mt-5 divide-y divide-[#D8D4CC]">
            {order.items.map((i) => (
              <div key={`${i.product_id}-${i.size || ""}`} className="flex gap-4 py-4">
                <img src={i.image} alt="" className="w-16 h-20 object-cover bg-[#EFECE5]" />
                <div className="flex-1">
                  <div className="font-serif text-[16px] text-[#1B3B36] leading-tight">{i.name}</div>
                  <div className="text-[12px] text-[#4A4A4A] mt-1">{i.size ? `Size ${i.size} ft · ` : ""}Qty {i.quantity}</div>
                </div>
                <div className="text-[14px] text-[#1B3B36]">₹{(i.price * i.quantity).toLocaleString("en-IN")}</div>
              </div>
            ))}
          </div>
          <dl className="mt-4 space-y-2 border-t border-[#D8D4CC] pt-4 text-[13px] text-[#4A4A4A]">
            <div className="flex justify-between"><dt>Subtotal</dt><dd>₹{order.subtotal.toLocaleString("en-IN")}</dd></div>
            <div className="flex justify-between"><dt>Shipping</dt><dd>{order.shipping === 0 ? "Free" : `₹${order.shipping}`}</dd></div>
            <div className="flex justify-between font-serif text-xl text-[#1B3B36] pt-3 border-t border-[#D8D4CC]"><dt>Total</dt><dd>₹{order.total.toLocaleString("en-IN")}</dd></div>
          </dl>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 text-[13px]">
            <div>
              <div className="overline">Shipping to</div>
              <div className="mt-2 text-[#4A4A4A] font-light leading-relaxed">
                {order.customer.name}<br />
                {order.shipping_address.line1}
                {order.shipping_address.line2 ? <>, {order.shipping_address.line2}</> : null}<br />
                {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.pincode}<br />
                {order.shipping_address.country}
              </div>
            </div>
            <div>
              <div className="overline">Contact</div>
              <div className="mt-2 text-[#4A4A4A] font-light leading-relaxed">
                {order.customer.email}<br />
                {order.customer.phone}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <a href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-[#1B3B36] text-[#1B3B36] px-6 py-3 text-[12px] uppercase tracking-[0.18em] hover:bg-[#1B3B36] hover:text-[#F9F8F6]">
            <MessageCircle size={13} /> WhatsApp our team
          </a>
          <Link to="/collections" className="inline-flex bg-[#1B3B36] text-[#F9F8F6] px-6 py-3 text-[12px] uppercase tracking-[0.18em] hover:bg-[#132A26]">
            Continue browsing
          </Link>
        </div>
      </section>
    </div>
  );
}
